import { z } from 'zod';

/**
 * A snapshot of what the platform currently is: what is running, what is in it,
 * and how it is configured.
 *
 * This exists so the admin "Structure" screen can show live facts next to the
 * static architecture explanation. A diagram that might be describing something
 * you decommissioned six months ago is worse than no diagram, so everything on
 * that page that *can* be read from the running system is read from it.
 *
 * Deliberately absent: hostnames, ports, connection strings, secret names.
 * Nothing here would help someone who got hold of it, and nothing here changes
 * if the deployment moves.
 */
export const platformSnapshotSchema = z.object({
  /** How long this API process has been up. Resets on every deploy. */
  uptimeSeconds: z.number().int(),
  environment: z.enum(['development', 'test', 'production']),
  databases: z.object({
    app: z.enum(['up', 'down']),
    sandbox: z.enum(['up', 'down']),
  }),
  /** Configuration an admin can act on, as booleans rather than raw values. */
  settings: z.object({
    publicRegistration: z.boolean(),
    /** Which source decided it — see registrationSettingSchema. */
    publicRegistrationSource: z.enum(['database', 'environment']),
    secureCookies: z.boolean(),
    sandboxStatementTimeoutMs: z.number().int(),
    sandboxMaxRows: z.number().int(),
  }),
  content: z.object({
    modules: z.number().int(),
    lessons: z.number().int(),
    /** Curriculum exercises — interview questions counted separately. */
    exercises: z.number().int(),
    interviewQuestions: z.number().int(),
    datasets: z.number().int(),
    badges: z.number().int(),
  }),
  people: z.object({
    learners: z.number().int(),
    coaches: z.number().int(),
    admins: z.number().int(),
    deactivated: z.number().int(),
    groups: z.number().int(),
  }),
});
export type PlatformSnapshot = z.infer<typeof platformSnapshotSchema>;

/**
 * Whether self-service sign-up is open, and which source decided that.
 *
 * `source` matters: a stored row overrides the `ALLOW_PUBLIC_REGISTRATION`
 * environment variable, so once the switch has been used, editing `.env` has no
 * further effect on this setting. Showing where the value came from is what
 * stops that being mysterious.
 */
export const registrationSettingSchema = z.object({
  open: z.boolean(),
  source: z.enum(['database', 'environment']),
  /** When an administrator last changed it; null while the environment decides. */
  updatedAt: z.string().nullable(),
});
export type RegistrationSetting = z.infer<typeof registrationSettingSchema>;

export const updateRegistrationSettingSchema = z.object({
  open: z.boolean(),
});
export type UpdateRegistrationSetting = z.infer<typeof updateRegistrationSettingSchema>;
